import type { ConnectorTestResultDTO, SftpConnectorDTO, SftpConnectorListDTO } from '@today/types'
import { apiClient } from './api.client'

/** A new connection: what the add form submits (no password, no key — TODAY generates the key pair). */
export interface SftpConnectorDraft {
	name: string
	host: string
	port: number
	username: string
}

/** Connectors are Super-Admin only (TO-393 Q3): there is no own-tenant route tree. TO-632: /sftp-connectors. */
const connectorsOf = (tenantId: string) => `/admin/tenants/${encodeURIComponent(tenantId)}/sftp-connectors`

/** A tenant's SFTP connectors (TO-393 REQ-014). */
export const sftpConnectorService = {
	/** `GET …/connectors` — oldest first. */
	async list(tenantId: string): Promise<SftpConnectorListDTO> {
		const { data } = await apiClient.get<SftpConnectorListDTO>(connectorsOf(tenantId))
		return data
	},

	/** `POST …/connectors` — returns the public key and egress IPs to send the customer. */
	async create(tenantId: string, draft: SftpConnectorDraft): Promise<SftpConnectorDTO> {
		const { data } = await apiClient.post<SftpConnectorDTO>(connectorsOf(tenantId), draft)
		return data
	},

	/** `POST …/connectors/{id}/test` — a failed connection is still a 200 with its code. */
	async test(tenantId: string, id: string): Promise<ConnectorTestResultDTO> {
		const { data } = await apiClient.post<ConnectorTestResultDTO>(`${connectorsOf(tenantId)}/${id}/test`)
		return data
	},

	/** `POST …/connectors/{id}/host-key/confirm` — trust the observed key on first use. */
	async confirmHostKey(tenantId: string, id: string, fingerprint: string): Promise<SftpConnectorDTO> {
		const { data } = await apiClient.post<SftpConnectorDTO>(`${connectorsOf(tenantId)}/${id}/host-key/confirm`, {
			fingerprint,
		})
		return data
	},

	/** `DELETE …/connectors/{id}` — refused while a logical file uses it. */
	async remove(tenantId: string, id: string): Promise<void> {
		await apiClient.delete(`${connectorsOf(tenantId)}/${id}`)
	},
}
